// CD: backend
// One controller for cards on home, search, collections and dynamically inserted recommendations.
const selector = '.pushe-card__img--hover';
const states = new WeakMap();
const hover = window.matchMedia('(hover:hover) and (pointer:fine)');

function prepare(image) {
  const state = states.get(image);
  if (!state || state.pending || state.ready) return;
  state.pending = true;
  image.loading = 'eager';
  if (image.getAttribute('data-pushe-srcset')) image.srcset = image.getAttribute('data-pushe-srcset');
  if (image.getAttribute('data-pushe-src')) image.src = image.getAttribute('data-pushe-src');
  image.removeAttribute('data-pushe-srcset');
  image.removeAttribute('data-pushe-src');
  state.source = image.src;
  state.srcset = image.srcset;
  if (image.complete) reveal(image, state);
}

async function reveal(image, state) {
  if (!image.complete || !image.naturalWidth || state.decoding || state.ready) return;
  state.decoding = true;
  try {
    // No decode support means keep the safe primary image.
    await image.decode();
    if (states.get(image) !== state || !image.isConnected || !image.naturalWidth) return;
    state.ready = true;
    image.closest('.pushe-card')?.setAttribute('data-pushe-hover-ready', '');
  } catch { /* Failed loads/decodes never hide the primary image. */ }
  finally { state.decoding = false; state.pending = false; }
}

function register(image) {
  if (states.has(image)) return;
  states.set(image, {});
  image.closest('.pushe-card')?.removeAttribute('data-pushe-hover-ready');
  image.addEventListener('load', () => reveal(image, states.get(image)));
  image.addEventListener('error', () => {
    states.set(image, {});
    image.closest('.pushe-card')?.removeAttribute('data-pushe-hover-ready');
  });
  if (image.complete && image.naturalWidth) reveal(image, states.get(image));
}

function eagerFirstRow(root) {
  const candidates = [...(root.querySelectorAll?.('img[data-pushe-eager-min]') || [])];
  if (root.matches?.('img[data-pushe-eager-min]')) candidates.push(root);
  for (const image of candidates) {
    const rect = image.getBoundingClientRect();
    if (innerWidth >= Number(image.dataset.pusheEagerMin) && rect.top < innerHeight && rect.bottom > 0) image.loading = 'eager';
  }
}

function scan(root) {
  eagerFirstRow(root);
  root.querySelectorAll?.('.pushe-gallery__slide[data-pushe-col]').forEach(gallerySize);
  if (root.matches?.(selector)) register(root);
  root.querySelectorAll?.(selector).forEach(register);
}

// The gallery controller assigns columns after filtering/reordering variant images.
// Keep responsive image selection aligned with that actual column, including a full-width tail.
function gallerySize(slide) {
  const desktop = { wide: '30vw', narrow: '20vw', full: '50vw' }[slide.dataset.pusheCol];
  const image = slide.querySelector('img');
  if (desktop && image) image.sizes = `(min-width: 900px) ${desktop}, calc(100vw - 2 * clamp(1.25rem, 4vw, 4rem))`;
}

function intent(event) {
  if (event.type === 'pointerover' && (event.pointerType === 'touch' || !hover.matches)) return;
  const image = event.target.closest?.('.pushe-card')?.querySelector(selector);
  if (image) { register(image); prepare(image); }
}

scan(document);
document.addEventListener('pointerover', intent);
document.addEventListener('focusin', intent);
new MutationObserver(records => {
  for (const record of records) {
    if (record.type === 'attributes') {
      if (record.attributeName === 'data-pushe-col') { gallerySize(record.target); continue; }
      const image = record.target;
      if (!image.matches(selector)) continue;
      // Activating our deferred URL is not a replacement; keep the pending decode state.
      const state = states.get(image);
      if (state?.pending && state.source === image.src && state.srcset === image.srcset) continue;
      image.closest('.pushe-card')?.removeAttribute('data-pushe-hover-ready');
      states.set(image, {});
      if (image.complete && image.naturalWidth) reveal(image, states.get(image));
    } else {
      for (const node of record.addedNodes) scan(node);
      const card = record.target.closest?.('.pushe-card');
      if (card && !card.querySelector(selector)) card.removeAttribute('data-pushe-hover-ready');
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'data-pushe-col'] });
